import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import TitlePinStar from './TitlePinStar';

/**
 * Puts the pin star (TitlePinStar) at the end of the page title — the first <h1> in the app's
 * main area — on every page, without each of the ~160 page headers drawing it.
 *
 * Mounted once by AppLayout. It keeps one slot element and appends it to the current title,
 * watching the main area so a title that renders late (after a load), changes (navigation) or is
 * re-rendered by React (which may drop or reorder foreign children) gets the slot back at its end.
 * A page without an <h1> shows no star; its pages can still be pinned from the sidebar.
 */
export default function PageTitlePin({ root = '.main-content' }) {
  const [slot, setSlot] = useState(null);
  const [heading, setHeading] = useState(null);

  useEffect(() => {
    const main = typeof document !== 'undefined' ? document.querySelector(root) : null;
    if (!main || typeof MutationObserver === 'undefined') return undefined;
    const box = document.createElement('span');
    box.className = 'title-pin-slot';
    let current = null;
    const place = () => {
      const h1 = main.querySelector('h1');
      if (h1 !== current) {
        current = h1;
        setHeading(h1);
      }
      if (!h1) {
        if (box.parentNode) box.remove();
        return;
      }
      if (h1.lastChild !== box) h1.appendChild(box);
    };
    place();
    setSlot(box);
    const observer = new MutationObserver(place);
    observer.observe(main, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      box.remove();
    };
  }, [root]);

  if (!slot || !heading) return null;
  return createPortal(<TitlePinStar heading={heading} slot={slot} />, slot);
}
